<template>
  <div>
    <c3-chart
      v-if="reportLoaded"
      :height="300"
      :data="chartData"
      :config="chartConfig"
      type='bar'/>
  </div>
</template>

<script>
import ReportRecord from '@/orm/models/ReportRecord'
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import Moment from 'moment-timezone'
import * as d3 from 'd3'
import { each, get, map, join } from 'lodash'

export default {
  name: 'SiteViolationReport',
  components: {
    C3Chart
  },
  props: {
    report: {
      type: ReportRecord
    }
  },
  data () {
    return {
      colors: {
        cold: '#3b5b94',
        hot: '#b4343e',
        'time-gap': '#FFA35C',
        'no-data': '#E7BF0A'
      },
      labelToParam: {
        'Hot Violation': 'hot',
        'Cold Violation': 'cold',
        'Excessive Gap in Data Violation': 'time-gap',
        'Missing Data Violation': 'no-data'
        // Add other mappings here
      }
    }
  },
  computed: {
    /**
     * Identifies if the report is loaded
     *
     * @return boolean
     */
    reportLoaded () {
      return this.report && this.report.results_generated != null
    },
    /**
     * Format the data in the manner required by C3.
     *
     * @return object
     */
    chartData () {
      const violationData = {}
      // For each violation type displayed, set up an array with the violation type as the key and the label as the first element.
      each(this.report.parameters.violationTypes, violationType => {
        violationData[violationType] = [this.violationLabel(violationType)]
      })

      // Create the columns by iterating through each period and processing the data.
      each(this.report.results, result => {
        each(this.report.parameters.violationTypes, violationType => {
          const violations = get(result, 'violations_by_type.' + violationType, 0)
          violationData[violationType].push(violations)
        })
      })

      // Set up the color map
      const colors = {}
      each(this.report.parameters.violationTypes, violationType => {
        colors[this.violationLabel(violationType)] = this.colors[violationType]
      })

      return {
        type: 'bar',
        columns: map(violationData, data => data),
        colors: colors,
        groups: [
          map(this.report.parameters.violationTypes, violationType => this.violationLabel(violationType))
        ],
        onclick: function (d) {
          if (d.id) { // if the clicked element is not a label
            const result = this.report.results[d.index]
            const query = {
              startedAfter: result.periodStart,
              startedBefore: result.periodEnd,
              violations: this.labelToParam[d.id] // select the violation type associated with the clicked color
            }
            const url = this.$router.resolve({
              path: '/route/list',
              query: query
            }).href
            window.open(url, '_blank')
          }
        }.bind(this)
      }
    },

    /**
     * Returns the C3 config for the chart
     *
     * @return object
     */
    chartConfig () {
      const labels = map(this.report.results, result => {
        const routesViolated = this.$t('reports_ui.site_violation_report.n_of_m_routes_violated', { n: result.violating_routes, m: result.total_routes })
        const date = Moment.tz(result.periodStart, this.report.parameters.timezone)
        return date.format('l') + ` (${routesViolated})`
      })

      return {
        legend: {
          position: 'inset'
        },
        axis: {
          x: {
            height: 100,
            type: 'category',
            categories: labels,
            onclick: function (d) {
              const result = this.report.results[d.index]
              const query = {
                startedAfter: result.periodStart,
                startedBefore: result.periodEnd,
                violations: join(this.report.parameters.violationTypes, ',') // select all violation types
              }
              const url = this.$router.resolve({
                path: '/route/list',
                query: query
              }).href
              window.open(url, '_blank')
            }.bind(this)
          },
          y: {
            tick: {
              format: d3.format('d')
            },
            label: {
              text: this.$t('reports_ui.site_violation_report.count_of_violations'),
              position: 'outer-middle'
            }
          }
        },
        onrendered: function () {
          // Select all x-axis labels
          const labels = d3.selectAll('.c3-axis-x .tick text')
          labels.style('cursor', 'pointer') // Change the cursor to a pointer when hovering over the labels
          labels.on('click', function (d, i) {
            // Trigger the onclick function when a label is clicked
            const result = this.report.results[i]
            const query = {
              startedAfter: result.periodStart,
              startedBefore: result.periodEnd,
              violations: join(this.report.parameters.violationTypes, ',') // select all violation types
            }
            const url = this.$router.resolve({
              path: '/route/list',
              query: query
            }).href
            window.open(url, '_blank')
          }.bind(this))
        }.bind(this),
      }
    }
  },
  methods: {
    /**
     * Convenience method to get the translated label of a violation type
     * (since we have to use it as a key in C3 so often.)
     * @param violationType
     * @returns {*}
     */
    violationLabel (violationType) {
      return this.$t('fatal_violation:' + violationType)
    }
  }
}
</script>

<style scoped>
</style>
